/**
 * 식장 복수 방문 기록 (localStorage).
 * 기존 Same 모달 키 `goi_parlor_visited_ids` 와 분리.
 *
 * multi(distinct ≥ 2) / non_multi 실험 모수 분리 + #334 방문 목록 칩.
 */

export const COMPARE_VISITS_KEY = "goi_parlor_compare_visits_v1";
export const VISITED_LIST_SUBMITTED_KEY = "goi_parlor_visited_list_submitted";

/** 방문 이력 TTL — 2곳 이상 봤는지 판정 */
export const COMPARE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** #334 제출 후 칩 재노출 억제 */
export const VISITED_LIST_SUPPRESS_MS = 24 * 60 * 60 * 1000;

const LEGACY_COMPARE_EXPERIMENT_KEYS = [
  "goi_parlor_compare_submitted",
  "goi_parlor_compare_shown_pair",
  "goi_parlor_compare_suppress_same",
];

export type CompareVisitItem = {
  id: string;
  name: string;
  /** 예: "부산 동래구" */
  region: string;
  ts: number;
  imageUrl?: string;
  /** 무빈소 기준 만원 */
  minPriceManwon?: number;
};

type CompareVisitsStore = {
  v: 1;
  items: CompareVisitItem[];
};

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function now() {
  return Date.now();
}

export function clearLegacyCompareExperimentKeys() {
  try {
    LEGACY_COMPARE_EXPERIMENT_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

export function pruneCompareVisits(
  items: CompareVisitItem[],
  ttlMs: number = COMPARE_TTL_MS,
  at: number = now(),
): CompareVisitItem[] {
  return items.filter((it) => at - it.ts <= ttlMs);
}

export function loadCompareVisits(): CompareVisitItem[] {
  try {
    clearLegacyCompareExperimentKeys();
    const data = safeParse<CompareVisitsStore>(localStorage.getItem(COMPARE_VISITS_KEY));
    if (!data?.items || !Array.isArray(data.items)) return [];
    return pruneCompareVisits(data.items);
  } catch {
    return [];
  }
}

export function saveCompareVisits(items: CompareVisitItem[]) {
  try {
    const pruned = pruneCompareVisits(items);
    const payload: CompareVisitsStore = { v: 1, items: pruned };
    localStorage.setItem(COMPARE_VISITS_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

/** 현재 식장 upsert 후 저장. 반환: prune 후 전체 목록 */
export function upsertCompareVisit(
  item: Omit<CompareVisitItem, "ts"> & { ts?: number },
): CompareVisitItem[] {
  const ts = item.ts ?? now();
  const prev = loadCompareVisits();
  const without = prev.filter((x) => x.id !== item.id);
  const next = pruneCompareVisits([
    ...without,
    {
      id: item.id,
      name: item.name,
      region: item.region,
      ts,
      imageUrl: item.imageUrl,
      minPriceManwon: item.minPriceManwon,
    },
  ]);
  saveCompareVisits(next);
  return next;
}

/** 최근 방문 순 상위 n개 */
export function getRecentCompareVisits(items: CompareVisitItem[], n = 2): CompareVisitItem[] {
  return [...items].sort((a, b) => b.ts - a.ts).slice(0, n);
}

/** multi path: distinct ≥ 2 */
export function hasMultiParlorComparePath(): boolean {
  return loadCompareVisits().length >= 2;
}

export function isVisitedListSubmitSuppressed(at: number = now()): boolean {
  try {
    const data = safeParse<{ ts: number }>(localStorage.getItem(VISITED_LIST_SUBMITTED_KEY));
    if (!data?.ts) return false;
    if (at - data.ts > VISITED_LIST_SUPPRESS_MS) {
      localStorage.removeItem(VISITED_LIST_SUBMITTED_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function markVisitedListSubmitted(at: number = now()) {
  try {
    localStorage.setItem(VISITED_LIST_SUBMITTED_KEY, JSON.stringify({ ts: at }));
  } catch {
    // ignore
  }
}
